/**
 * Calendar-Aware Absence Planner (Phase 4 — Step 4.3).
 *
 * Mini calendar in What-If panel to select future sessions and simulate their impact.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useAppStore } from '@/store';
import { simulateFutureAbsences } from '@abs/shared-logic';
import { computeTotalSessions, getPastSessionCount, getRemainingSessionCount } from '@abs/shared-logic';
import { computeRecoveryPath, deriveWarningThreshold } from '@abs/shared-logic';
import { todayISO } from '@abs/shared-logic';
import type { AbsenceEntry, Course, SessionDate } from '@abs/shared-types';
import { StatCard } from '../shared';

interface CalendarAwarePlannerProps {
  course: Course;
}

export function CalendarAwarePlanner({ course }: CalendarAwarePlannerProps): React.ReactElement {
  const policy = useAppStore((s) => s.policies.find((p) => p.id === course.policyId));
  const entries = useAppStore((s) => s.entries.filter((e: AbsenceEntry) => e.courseId === course.id));

  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<'pick' | 'trip'>('pick');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  const { sessions, total } = useMemo(
    () => computeTotalSessions(course.meetingPatterns, course.sessionOverrides, course.startDate, course.endDate),
    [course],
  );

  // Future sessions only
  const today = todayISO();
  const futureSessions = useMemo(
    () => sessions.filter((s: SessionDate) => s.date > today),
    [sessions, today],
  );

  const toggleDate = useCallback((date: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedDates(new Set(futureSessions.map((s: SessionDate) => s.date)));
  }, [futureSessions]);

  const clearAll = useCallback(() => {
    setSelectedDates(new Set());
  }, []);

  // Trip-mode: select all future sessions within [rangeStart, rangeEnd]
  const applyTripRange = useCallback((start: string, end: string) => {
    if (!start && !end) {
      setSelectedDates(new Set());
      return;
    }
    const lo = start || end;
    const hi = end || start;
    const inRange = futureSessions
      .filter((s) => s.date >= lo && s.date <= hi)
      .map((s) => s.date);
    setSelectedDates(new Set(inRange));
  }, [futureSessions]);

  // Simulate the impact
  const impact = useMemo(() => {
    if (!policy || selectedDates.size === 0) return null;
    return simulateFutureAbsences(
      course, policy, entries, total,
      selectedDates.size, course.baseGrade,
    );
  }, [course, policy, entries, selectedDates]);

  // Recovery path after the trip (TODO-15)
  const recovery = useMemo(() => {
    if (mode !== 'trip' || !policy || !impact || selectedDates.size === 0) return null;
    const inRange = selectedDates.size;
    const attended = Math.max(0, getPastSessionCount(sessions) - inRange);
    const remaining = Math.max(0, getRemainingSessionCount(sessions) - inRange);
    const warn = deriveWarningThreshold(total, policy.tiers);
    return computeRecoveryPath(total, attended, remaining, warn);
  }, [mode, impact, selectedDates, sessions, total, policy]);

  // Group future sessions by month for display
  const groupedByMonth = useMemo(() => {
    const groups = new Map<string, SessionDate[]>();
    for (const s of futureSessions) {
      const month = s.date.slice(0, 7); // YYYY-MM
      if (!groups.has(month)) groups.set(month, []);
      groups.get(month)!.push(s);
    }
    return groups;
  }, [futureSessions]);

  if (!policy) {
    return <p>No policy configured.</p>;
  }

  return (
    <div className="calendar-planner" role="region" aria-label="Calendar-Aware Absence Planning">
      <div className="calendar-planner__mode" role="radiogroup" aria-label="Planning mode">
        <label>
          <input
            type="radio"
            name={`planner-mode-${course.id}`}
            checked={mode === 'pick'}
            onChange={() => { setMode('pick'); setRangeStart(''); setRangeEnd(''); setSelectedDates(new Set()); }}
          />{' '}
          Pick sessions
        </label>
        <label>
          <input
            type="radio"
            name={`planner-mode-${course.id}`}
            checked={mode === 'trip'}
            onChange={() => setMode('trip')}
          />{' '}
          Plan a trip/event
        </label>
      </div>

      {mode === 'trip' ? (
        <div className="calendar-planner__trip">
          <h5>Select a date range to simulate</h5>
          <label>
            From
            <input
              type="date"
              value={rangeStart}
              min={today}
              onChange={(e) => { setRangeStart(e.target.value); applyTripRange(e.target.value, rangeEnd); }}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={rangeEnd}
              min={rangeStart || today}
              onChange={(e) => { setRangeEnd(e.target.value); applyTripRange(rangeStart, e.target.value); }}
            />
          </label>
          <span className="calendar-planner__count">{selectedDates.size} sessions in range</span>
        </div>
      ) : (
        <>
          <h5>Select future sessions to simulate</h5>
          <div className="calendar-planner__actions">
            <button type="button" onClick={selectAll}>Select All</button>
            <button type="button" onClick={clearAll}>Clear</button>
            <span className="calendar-planner__count">{selectedDates.size} selected</span>
          </div>

          <div className="calendar-planner__months">
            {Array.from(groupedByMonth.entries()).map(([month, sessionsInMonth]) => (
              <div key={month} className="calendar-planner__month">
                <h6>{month}</h6>
                <div className="calendar-planner__dates">
                  {sessionsInMonth.map((s: SessionDate) => (
                    <button
                      key={s.date}
                      type="button"
                      className={`calendar-planner__date ${selectedDates.has(s.date) ? 'calendar-planner__date--selected' : ''}`}
                      onClick={() => toggleDate(s.date)}
                      aria-pressed={selectedDates.has(s.date)}
                    >
                      {s.date.slice(8)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {futureSessions.length === 0 && (
              <p className="calendar-planner__empty">No future sessions remaining.</p>
            )}
          </div>
        </>
      )}

      {impact && (
        <div className="calendar-planner__impact" role="status" aria-live="polite">
          <h5>Projected Impact</h5>
          <div className="calendar-planner__stats">
            <StatCard
              size="sm"
              value={`${impact.projectedPercentage.toFixed(1)}%`}
              label="Attendance"
            />
            {impact.projectedGrade && (
              <StatCard size="sm" value={impact.projectedGrade} label="Grade" />
            )}
            <StatCard
              size="sm"
              tone={impact.isAutoFail ? 'danger' : 'default'}
              value={impact.isAutoFail ? 'AUTO-FAIL' : impact.projectedRiskLevel}
              label="Risk"
            />
            {impact.tierTriggered && (
              <StatCard size="sm" tone="danger" value={impact.tierTriggered.description} label="Penalty" />
            )}
          </div>
          {recovery && (
            <div className="calendar-planner__recovery">
              <p className="stat-caption">
                After this trip: attend the next <strong>{recovery.sessionsNeeded}</strong> sessions to return to safe zone. ({recovery.isRecoverable ? 'Recoverable' : 'Recovery not possible'}).
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
